/** @shell standard @header bar */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, Image } from 'react-native';
import Animated from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Clickable } from '../components/common/Clickable';
import { SvgIcon } from '../components/common/SvgIcon';
import { ScreenHeader, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { fetchCmsPages, filterNewsPages, resolveCmsPageLocalizedHtml } from '../data/domains/config';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState } from '../components/common/ErrorState';
import { useNavigation } from '@react-navigation/native';
import { useOrganizationSettings } from '../data/domains/config';
import { useTranslation } from '../shared/i18n';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { cmsPageHtmlToDisplayText } from '../shared/utils/htmlToPlainText';
import { pickCmsPageImageUri } from '../shared/utils/resolveCmsImageUri';
import { androidAppHeaderContentTop } from '../shared/theme/sharedStyles';
import {
  StackScreenShell,
  studioCardShell,
  DASH_STUDIO_RADIUS,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
} from '../shared/theme/studio/stackScreenShell';
import moment from 'moment';

const PLACEHOLDER_IMG = 'https://via.placeholder.com/400x200?text=News';

function createNewsScreenStyles(c: ThemeColors) {
  const primary_ultra = (c as { primaryUltra?: string }).primaryUltra ?? c.primary + '18';
  return StyleSheet.create({
    content: {
      flex: 1,
    },
    scrollContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      gap: STACK_SECTION_GAP,
    },
    banner: {
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      paddingVertical: appTheme.spacing.xl,
      paddingHorizontal: appTheme.spacing.lg,
      alignItems: 'center',
    },
    bannerIconWrap: {
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: primary_ultra,
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: appTheme.spacing.sm,
    },
    bannerTitle: {
      ...appTheme.typography.h3,
      color: c.text,
      marginBottom: appTheme.spacing.xs,
    },
    bannerText: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      textAlign: 'center',
    },
    newsCard: {
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      overflow: 'hidden',
    },
    newsImage: {
      width: '100%',
      height: 180,
      backgroundColor: c.borderLight,
    },
    newsContent: {
      padding: appTheme.spacing.lg,
    },
    newsHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: appTheme.spacing.sm,
    },
    categoryBadge: {
      backgroundColor: c.primary + '18',
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: 4,
      borderRadius: appTheme.borderRadius.sm,
    },
    categoryText: {
      ...appTheme.typography.captionSmall,
      color: c.primary,
      fontWeight: '600',
      textTransform: 'uppercase',
    },
    newsDate: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
    },
    newsTitle: {
      ...appTheme.typography.body,
      fontSize: 17,
      fontWeight: '700',
      color: c.text,
      marginBottom: appTheme.spacing.sm,
    },
    newsExcerpt: {
      ...appTheme.typography.bodySmall,
      color: c.textSecondary,
      lineHeight: 22,
      marginBottom: appTheme.spacing.md,
    },
    newsFooter: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    readMoreText: {
      ...appTheme.typography.bodySmall,
      color: c.primary,
      fontWeight: '600',
      marginRight: appTheme.spacing.xs,
    },
    emptyState: {
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: appTheme.spacing.xxl * 2,
      paddingHorizontal: appTheme.spacing.lg,
    },
    emptyIconWrap: {
      width: 80,
      height: 80,
      borderRadius: 40,
      backgroundColor: c.borderLight,
      justifyContent: 'center',
      alignItems: 'center',
      marginBottom: appTheme.spacing.lg,
    },
    emptyTitle: {
      ...appTheme.typography.h3,
      color: c.text,
      marginBottom: appTheme.spacing.sm,
    },
    emptyText: {
      ...appTheme.typography.body,
      color: c.textSecondary,
      textAlign: 'center',
    },
    stateWrap: {
      flex: 1,
    },
  });
}

interface NewsCardPropsWithT {
  news: any;
  onPress: () => void;
  categoryFallback: string;
  readMoreText: string;
  styles: ReturnType<typeof createNewsScreenStyles>;
  colors: ThemeColors;
}

const NewsCard: React.FC<NewsCardPropsWithT> = ({ news, onPress, categoryFallback, readMoreText, styles, colors }) => {
  const img_uri = (news as { resolvedImageUri?: string | null }).resolvedImageUri ?? pickCmsPageImageUri(news as Record<string, unknown>);
  return (
    <Clickable style={styles.newsCard} onPress={onPress} hitSlop={0} accessibilityLabel={news.title || news.meta_title || readMoreText} accessibilityRole="button">
      <Image source={{ uri: img_uri || PLACEHOLDER_IMG }} style={styles.newsImage} resizeMode="cover" />
      <View style={styles.newsContent}>
        <View style={styles.newsHeader}>
          <View style={styles.categoryBadge}>
            <Text style={styles.categoryText}>{news.category || categoryFallback}</Text>
          </View>
          <Text style={styles.newsDate}>{news.published_at || news.created_at ? moment(news.published_at || news.created_at).format('MMM D, YYYY') : ''}</Text>
        </View>

        <Text style={styles.newsTitle} numberOfLines={2}>
          {news.title || news.meta_title}
        </Text>
        <Text style={styles.newsExcerpt} numberOfLines={3}>
          {cmsPageHtmlToDisplayText(String(news.excerpt || news.meta_description || ''))}
        </Text>

        <View style={styles.newsFooter}>
          <Text style={styles.readMoreText}>{readMoreText}</Text>
          <SvgIcon name="arrow-forward" size={18} color={colors.primary} />
        </View>
      </View>
    </Clickable>
  );
};

export const NewsScreen: React.FC = () => {
  const navigation = useAuthNavigation();
  const go_back = useExitOrGoBack();
  const insets = useSafeAreaInsets();
  const content_top = androidAppHeaderContentTop(insets.top);
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const { t } = useTranslation();
  const [newsItems, setNewsItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { settings: appSettings } = useOrganizationSettings();
  const appLanguage = (appSettings?.appLanguage ?? appSettings?.language) === 'ar' ? 'ar' : 'en';

  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createNewsScreenStyles(colors), [colors]);

  const fetchNews = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const pages = await fetchCmsPages();
      const list = filterNewsPages(pages);
      const processedNews = list.map((item: any) => ({
        ...item,
        displayContent: resolveCmsPageLocalizedHtml(item, appLanguage),
        title: item.title ?? item.meta_title ?? item.name,
        excerpt: item.excerpt ?? item.meta_description ?? '',
        image: item.image ?? item.featured_image ?? item.thumbnail,
        published_at: item.published_at ?? item.updated_at ?? item.created_at,
        /** Resolved absolute URL for list + detail (pickCmsPageImageUri also run in NewsCard). */
        resolvedImageUri: pickCmsPageImageUri(item),
      }));
      setNewsItems(processedNews);
    } catch (err: any) {
      setError(err?.message || t('news.failedToLoad'));
      setNewsItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [appLanguage, t]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const handleNewsPress = useCallback(
    (news: any) => {
      navigation.navigate('NewsArticle', { article: news });
    },
    [navigation],
  );

  if (isLoading) {
    return (
      <StackScreenShell colors={colors}>
        <ScreenHeader title={t('news.title')} onBack={go_back} scrollY={scrollY} />
        <View style={[styles.stateWrap, { paddingTop: content_top }]}>
          <LoadingState message={t('news.loading')} />
        </View>
      </StackScreenShell>
    );
  }

  if (error) {
    return (
      <StackScreenShell colors={colors}>
        <ScreenHeader title={t('news.title')} onBack={go_back} scrollY={scrollY} />
        <View style={[styles.stateWrap, { paddingTop: content_top }]}>
          <ErrorState
            title={t('common.error')}
            message={error}
            onRetry={fetchNews}
            issue_context={{
              page: t('news.title'),
              issue: 'Failed to load news',
              kind: 'api',
              api_endpoint: 'pages',
              http_method: 'GET',
            }}
          />
        </View>
      </StackScreenShell>
    );
  }

  return (
    <StackScreenShell colors={colors}>
      <ScreenHeader title={t('news.title')} onBack={go_back} scrollY={scrollY} />
      <Animated.ScrollView
        style={styles.content}
        onScroll={onScroll}
        scrollEventThrottle={16}
        contentContainerStyle={[
          styles.scrollContent,
          {
            paddingTop: content_top,
            paddingBottom: insets.bottom + STACK_SECTION_GAP * 2,
          },
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        <View style={styles.banner}>
          <View style={styles.bannerIconWrap}>
            <SvgIcon name="newspaper" size={28} color={colors.primary} />
          </View>
          <Text style={styles.bannerTitle}>{t('news.stayUpdated')}</Text>
          <Text style={styles.bannerText}>{t('news.bannerText')}</Text>
        </View>

        {newsItems.length > 0 ? (
          newsItems.map((news, idx) => (
            <NewsCard
              key={news.id ?? news.slug ?? `news-${idx}`}
              news={{ ...news, title: news.title || t('news.articleFallback') }}
              onPress={() => handleNewsPress(news)}
              categoryFallback={t('news.categoryFallback')}
              readMoreText={t('news.readMore')}
              styles={styles}
              colors={colors}
            />
          ))
        ) : (
          <View style={styles.emptyState}>
            <View style={styles.emptyIconWrap}>
              <SvgIcon name="newspaper" size={48} color={colors.textLight} />
            </View>
            <Text style={styles.emptyTitle}>{t('news.noNews')}</Text>
            <Text style={styles.emptyText}>{t('news.checkBack')}</Text>
          </View>
        )}
      </Animated.ScrollView>
    </StackScreenShell>
  );
};

export default NewsScreen;
